/**
 * Account-linking pending state, persisted in sessionStorage between the
 * auth-form (where we detect the provider conflict) and the /link-account
 * route (where we ask the user to authenticate with their original provider
 * so we can link the new credential to it).
 *
 * sessionStorage scope: cleared when the tab closes; we also clear explicitly
 * after a successful link to keep the password / credential JSON off disk.
 */

const KEY = "acruxsoft.pendingLink";

export type PendingLink =
  | {
      // Conflict path: user clicked "Continue with Google", but their account
      // was originally created with email+password. We hold the Google cred
      // in JSON form and ask for the password to re-authenticate, then link.
      mode: "add-google";
      email: string;
      googleCredJson: string;
    }
  | {
      // Conflict path: user filled email+password to sign up, but the email
      // already exists as a Google account. We hold the chosen password and
      // ask the user to sign in with Google, then link the password creds.
      mode: "add-password";
      email: string;
      pendingPassword: string;
    };

export function savePendingLink(data: PendingLink): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(data));
  } catch (e) {
    console.warn("[account-linking] sessionStorage write failed", e);
  }
}

export function readPendingLink(): PendingLink | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingLink;
  } catch (e) {
    console.warn("[account-linking] sessionStorage read failed", e);
    return null;
  }
}

export function clearPendingLink(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
